import {call} from '@redux-saga/core/effects';
import {createSnackbar} from 'Core/ducks/snackbar';
import {isLoaded, Loaded, Loading} from 'Core/models/Async';
import {ValidatableEmail, ValidEmail} from 'Core/models/Email';
import {NonEmptyString} from 'Core/models/EmptyString';
import {ValidatableNonEmptyString} from 'Core/models/ValidatableNonEmptyString';
import {isRight} from 'fp-ts/lib/Either';
import {all, put, takeLatest} from 'redux-saga/effects';
import {getValidationSaga} from 'Shared/utilities/sagaApiGenerators';
import {AddBeneficiaryUserDto, AddBusinessOperationsSupportDto} from 'Users/api';
import bosAndBeneficiariesUserCreationApi from 'Users/api/bosAndBeneficiariesUserCreationApi';
import userApi from 'Users/api/userApi';
import {
  addBeneficiariesUser,
  addBOSToolsUser,
  BOSAndBeneficiariesUserCreationActionTypes,
  changeUser,
  DEFAULT_STATE,
  updateField,
} from 'Users/ducks/bosAndBeneficiariesUserCreation';
import {BOSAndBeneficiariesUser} from 'Users/models/BOSAndBeneficiariesUser';

// Since both BOS Tools and Beneficiary has only one user type, their roleID will be 1
const RoleID = 1;

const mapUserToDto = (user: BOSAndBeneficiariesUser): AddBeneficiaryUserDto | AddBusinessOperationsSupportDto | false =>
  (user.email &&
    user.username &&
    isLoaded(user.email) &&
    isLoaded(user.username) &&
    isRight(user.email.Data) &&
    isRight(user.username.Data) && {
      email: user.email.Data.right.address,
      userName: user.username?.Data.right.value,
      firstName: user.firstName?.value,
      lastName: user.lastName?.value,
      mobilePhone: user.phoneNumber?.phoneNumber,
      roleIds: [RoleID],
    }) ||
  false;

function* validateUsernameSaga() {
  yield takeLatest(
    BOSAndBeneficiariesUserCreationActionTypes.VALIDATE_USERNAME,
    getValidationSaga(
      NonEmptyString.create,
      username => updateField({username: username as ValidatableNonEmptyString}),
      userApi.validateAllstateUsername
    )
  );
}

function* validateEmailSaga() {
  yield takeLatest(
    BOSAndBeneficiariesUserCreationActionTypes.VALIDATE_EMAIL,
    getValidationSaga(
      ValidEmail.create,
      email => updateField({email: email as ValidatableEmail}),
      userApi.validateEmail
    )
  );
}

function* addBeneficiariesUserApi(action: ReturnType<typeof addBeneficiariesUser>) {
  const userDto = mapUserToDto(action.payload.user);

  if (userDto) {
    yield put(changeUser(action.payload.user as Loading<BOSAndBeneficiariesUser>));

    const result = yield call(
      bosAndBeneficiariesUserCreationApi.addBeneficiariesUser,
      userDto as AddBeneficiaryUserDto
    );

    if (isRight(result)) {
      yield put(changeUser(DEFAULT_STATE.bosAndBeneficiariesUser as Loaded<BOSAndBeneficiariesUser>));
      yield put(createSnackbar('Beneficiary user successfully created'));
    } else {
      yield put(changeUser(new Loaded(action.payload.user)));
      yield put(createSnackbar('Failed to create Beneficiary user'));
    }
  } else {
    yield put(createSnackbar('Invalid user, please check all fields'));
  }
}

function* addBeneficiariesUserSaga() {
  yield takeLatest(BOSAndBeneficiariesUserCreationActionTypes.ADD_BENEFICIARIES_USER, addBeneficiariesUserApi);
}

function* addBOSToolsUserApi(action: ReturnType<typeof addBOSToolsUser>) {
  const userDto = mapUserToDto(action.payload.user);

  if (userDto) {
    yield put(changeUser(action.payload.user as Loading<BOSAndBeneficiariesUser>));

    const result = yield call(
      bosAndBeneficiariesUserCreationApi.addBOSToolsUser,
      userDto as AddBusinessOperationsSupportDto
    );

    if (isRight(result)) {
      yield put(changeUser(DEFAULT_STATE.bosAndBeneficiariesUser as Loaded<BOSAndBeneficiariesUser>));
      yield put(createSnackbar('BOS Tools user successfully created'));
    } else {
      yield put(changeUser(new Loaded(action.payload.user)));
      yield put(createSnackbar('Failed to create BOS Tools user'));
    }
  } else {
    yield put(createSnackbar('Invalid user, please check all fields'));
  }
}

function* addBOSToolsUserSaga() {
  yield takeLatest(BOSAndBeneficiariesUserCreationActionTypes.ADD_BOSTOOLS_USER, addBOSToolsUserApi);
}

export default function*() {
  yield all([validateUsernameSaga(), validateEmailSaga(), addBeneficiariesUserSaga(), addBOSToolsUserSaga()]);
}
